/**
 * ขอแก้ยอดเบิก — the per-row control and its form, on the นำเข้ารายงานตามเบิกประกัน run page.
 *
 * It lives HERE, next to the parked row, because the evidence is the row: the payer's
 * figure, the CRM's figure, the voucher code, the billing id and the file they came from.
 * A separate approval queue would have to rebuild all of that, which is why the owner put
 * the decision on this page and asked for no notification (2026-08-25) — the GM opens the
 * run they were going to open anyway.
 *
 * ⚠️ TWO REASONS, AND ONLY ONE OF THEM TOUCHES MONEY. `amount_mismatch` covers two
 * different problems that look identical in a table:
 *
 *   บันทึกยอดผิด        staff typed the wrong price or picked the wrong catalogue row.
 *                       Correcting priceAdvance IS the fix → goes to a GM/Admin.
 *
 *   ยังเปิดบิลไม่ครบ     the payer paid for work only half-billed. [measured] billing
 *                       1312037 holds ฿1,179 against a ฿2,357.21 GoWabi retainer voucher,
 *                       N=1, DF ฿575, sole line on its treatment — because the second half
 *                       is billed at the collection visit, a different treatment entirely.
 *                       ⚠️ "Correcting" it to ฿2,357.21 puts two visits' revenue on one
 *                       line and leaves DF at ฿575 where two lines pay ฿1,150 — the doctor
 *                       loses ฿575, quietly. So this reason writes NOTHING. It records the
 *                       finding and sends someone to the chart to bill the rest.
 *
 * That second list is not a consolation prize: it is money the clinic has already earned
 * and already been paid for, and nothing else in the CRM surfaces it.
 */
import { useState } from 'react'
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react'
import { apiErrorFromBody } from '../../lib/apiError.js'

/** ⚠️ Mirrored from functions/lib/claimAmountRequest.js. Separate bundles — see role.js. */
/**
 * ⚠️ THE FOUR PAYERS THAT CARRY A SIBLING FEE LINE. autofeeBilling writes a billing_type 340
 * line for these and not for Gowabi, and its priceDoctor is 1% of the claim — so on THESE,
 * approving an amount change leaves the doctor deduction computed from the OLD figure.
 * api 638 does not recompute it (owner decision 2026-09-14: warn, do not block) and stamps
 * the drift into billing.log[]. This list must stay in step with that endpoint.
 */
const DF_FEE_PAYERS = new Set(['Generali', 'AIA', 'AXA', 'Prudential'])
export const dfDriftPayer = (payer) => DF_FEE_PAYERS.has(String(payer || '').trim())

const REASONS = [
  {
    key: 'wrong_amount',
    label: 'บันทึกยอดผิด',
    hint: 'ยอดเบิกที่บันทึกไว้ในระบบไม่ถูกต้อง — ขอแก้เป็นยอดตามไฟล์',
    movesMoney: true,
  },
  {
    key: 'not_fully_billed',
    label: 'ยังเปิดบิลไม่ครบ',
    hint: 'ผู้จ่ายจ่ายเต็มจำนวนแล้ว แต่ในระบบยังเปิดบิลไม่ครบ (เช่น รีเทนเนอร์ที่แบ่งเก็บสองครั้ง)',
    movesMoney: false,
  },
]

const STATUS = {
  pending: { label: 'รออนุมัติ', cls: 'bg-amber-100 text-amber-800' },
  approved: { label: 'อนุมัติแล้ว', cls: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'ไม่อนุมัติ', cls: 'bg-rose-100 text-rose-800' },
  noted: { label: 'บันทึกไว้', cls: 'bg-slate-200 text-slate-700' },
  cancelled: { label: 'ยกเลิก', cls: 'bg-slate-100 text-slate-500' },
}

const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { maximumFractionDigits: 2 })

/* ── the form ──────────────────────────────────────────────────────────────── */

export function AmountRequestDialog({ row, onClose, onDone }) {
  // Default to the payer's figure — it is the number the operator is looking at and the
  // one they almost always mean. Still editable: a settlement can be wrong too.
  const [reason, setReason] = useState('wrong_amount')
  const [amount, setAmount] = useState(String(row.file_amount_paid ?? ''))
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const spec = REASONS.find((r) => r.key === reason)
  const movesMoney = !!spec?.movesMoney
  const amountOk = !movesMoney || (amount.trim() !== '' && Number.isFinite(Number(amount)) && Number(amount) >= 0)
  // หมายเหตุ is optional — owner, 2026-09-15. See validateCreate() for why.
  const ready = amountOk && !busy

  async function submit() {
    setBusy(true); setErr('')
    try {
      const res = await fetch('/api/claim-import/amount-request', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          row_id: row.id,
          reason,
          amount_after: movesMoney ? Number(amount) : 0,
          note: note.trim(),
        }),
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) { setErr(apiErrorFromBody(j, res.status, 'ส่งคำขอไม่สำเร็จ')); return }
      onDone?.()
      onClose?.()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 p-5">
        <div className="text-[15.5px] font-semibold text-slate-900">ขอแก้ยอดเบิก</div>

        <div className="mt-3 rounded-lg bg-slate-50 ring-1 ring-slate-200 px-3 py-2 text-[12.5px] text-slate-600">
          <div>billing #{row.billing_id} · {row.hn} {row.crm_name}</div>
          {/* ⚠️ The CRM figure comes from `reason_detail`, not from a field of its own —
              a staged row carries the payer's amount but NOT the clinic's, so the
              matcher's own "ไฟล์ X · ระบบ Y" sentence is the only place both appear.
              Worth a `crm_amount` column on table 103 one day; not worth a schema change
              for a label. */}
          <div className="mt-1 tabular-nums">
            ไฟล์ผู้จ่าย <b className="text-slate-900">{fmt(row.file_amount_paid)}</b>
          </div>
          {row.reason_detail ? <div className="mt-1">{row.reason_detail}</div> : null}
          {row.claim_ref ? <div className="mt-1">รหัส <code>{row.claim_ref}</code></div> : null}
        </div>

        <div className="mt-4 space-y-2">
          {REASONS.map((r) => (
            <label key={r.key} className={`block rounded-lg border px-3 py-2 cursor-pointer ${reason === r.key ? 'border-brand-400 bg-brand-50/50' : 'border-slate-200'}`}>
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="amount-reason"
                  className="mt-1"
                  checked={reason === r.key}
                  onChange={() => setReason(r.key)}
                />
                <div>
                  <div className="text-[13.5px] font-medium text-slate-800">{r.label}</div>
                  <div className="text-[12px] text-slate-500 leading-relaxed">{r.hint}</div>
                </div>
              </div>
            </label>
          ))}
        </div>

        {movesMoney ? (
          <div className="mt-4">
            <label className="block text-[12.5px] text-slate-600 mb-1">ยอดเบิกใหม่ (บาท)</label>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              className="h-9 w-48 rounded-lg border border-slate-300 px-2 text-[13.5px] tabular-nums"
            />
            <p className="mt-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              ระบบจะแก้ <b>ยอดเบิก</b>, <b>รวม</b> และ <b>ส่วนคลินิก</b> ให้สอดคล้องกัน
              {' · '}ต้องให้ GM หรือ Admin กดอนุมัติก่อนจึงจะมีผล
              {/* ⚠️ This used to say ค่า DF ของหมอไม่เปลี่ยน flatly. True for Gowabi, and a
                  lie for the four payers that carry a fee line — there the DF SHOULD move
                  and does not, which is the whole problem. */}
              {dfDriftPayer(row?.payer) ? (
                <span className="block mt-1.5 text-rose-800">
                  ⚠️ ผู้จ่ายรายนี้มีบรรทัดค่าธรรมเนียม 1% — ระบบ<b>ยังไม่คำนวณค่าตอบแทนหมอใหม่</b>ให้
                  ต้องไปตรวจเองที่สรุปเงินเดือน
                </span>
              ) : (
                <span className="block mt-1.5">ผู้จ่ายรายนี้ไม่มีบรรทัดค่าธรรมเนียม · ค่า DF ของหมอไม่เปลี่ยน</span>
              )}
            </p>
          </div>
        ) : (
          <p className="mt-4 text-[12px] text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 leading-relaxed">
            <AlertTriangle className="inline w-3.5 h-3.5 mr-1 -mt-0.5 text-amber-600" />
            <b>จะไม่มีการแก้ยอดใด ๆ</b> — การแก้ยอดในกรณีนี้จะทำให้รายได้สองครั้งไปกองอยู่บรรทัดเดียว
            และ <b>ค่า DF ของหมอหายไปหนึ่งชุด</b> ระบบจะบันทึกไว้เป็นรายการติดตาม เพื่อให้ไปเปิดบิลส่วนที่เหลือที่หน้าคนไข้
          </p>
        )}

        <div className="mt-3">
          <label className="block text-[12.5px] text-slate-600 mb-1">
            หมายเหตุ <span className="text-slate-400">(ไม่บังคับ)</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-[13px]"
            placeholder={movesMoney ? 'เช่น พนักงานคีย์ราคาผิดจากรายการในแอป' : ''}
          />
        </div>

        {err ? <div className="mt-2 text-[12.5px] text-rose-700">{err}</div> : null}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg text-[13.5px] text-slate-600 hover:bg-slate-100">ยกเลิก</button>
          <button
            type="button"
            disabled={!ready}
            onClick={submit}
            className="h-9 px-4 rounded-lg text-[13.5px] text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (movesMoney ? 'ส่งคำขอ' : 'บันทึกไว้')}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ── the per-row control ───────────────────────────────────────────────────── */

/**
 * Renders whatever this row needs: a request button, a status chip, or the approve /
 * reject pair. `req` is the existing request for this row, if any.
 */
export function AmountRequestCell({ row, req, payer, mayRequest, mayApprove, meId, onOpen, onDecided }) {
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')

  async function decide(decision) {
    // A refusal has to say why; an approval is carried by the approver's own name.
    let decision_note = ''
    if (decision === 'reject') {
      const el = document.getElementById(`amtreq-note-${req.id}`)
      decision_note = String(el?.value ?? '').trim()
      if (decision_note.length < 3) { setErr('ต้องระบุเหตุผลที่ไม่อนุมัติ'); return }
    }
    setBusy(decision); setErr('')
    try {
      const res = await fetch(`/api/claim-import/amount-request/${req.id}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, decision_note }),
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) { setErr(apiErrorFromBody(j, res.status, 'บันทึกผลไม่สำเร็จ')); return }
      onDecided?.()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy('')
    }
  }

  if (!req) {
    if (!mayRequest || !row.billing_id) return null
    return (
      <button type="button" onClick={onOpen} className="text-brand-700 hover:underline whitespace-nowrap">
        ขอแก้ยอด
      </button>
    )
  }

  const st = STATUS[req.status] || STATUS.pending
  const selfApproved = req.status === 'approved' && req.decided_by && req.decided_by === req.created_by

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`rounded px-1.5 py-0.5 text-[11px] ${st.cls}`}>{st.label}</span>
        {req.reason === 'not_fully_billed' ? <span className="text-[11px] text-slate-500">ยังเปิดบิลไม่ครบ</span> : null}
        {req.reason === 'wrong_amount' && req.status !== 'rejected' ? (
          <span className="text-[11px] text-slate-500 tabular-nums">
            {fmt(req.amount_before)} → {fmt(req.amount_after)}
          </span>
        ) : null}
        {/* ⚠️ Said out loud rather than hidden. Self-approval is permitted; it is still
            worth a reader knowing that one person did both halves. */}
        {selfApproved ? <span className="text-[11px] text-amber-700">อนุมัติเอง</span> : null}
      </div>

      {req.note ? <div className="text-[11.5px] text-slate-500">“{req.note}”</div> : null}

      {req.reason === 'not_fully_billed' && row.patient_id ? (
        <a
          href={`/patient/${row.patient_id}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11.5px] text-brand-700 hover:underline"
        >
          เปิดหน้าคนไข้เพื่อเปิดบิลส่วนที่เหลือ <ExternalLink className="w-3 h-3" />
        </a>
      ) : null}

      {/* ⚠️ BEFORE THE CLICK, not only in billing.log[] afterwards. Approving here changes
          what the claim is worth but does NOT recompute the sibling 1% fee line, so the
          deduction taken from the doctor stays on the old figure. Owner decision
          2026-09-14: warn and allow, rather than block the four main insurers. */}
      {req.status === 'pending' && mayApprove && req.reason === 'wrong_amount' && dfDriftPayer(payer) ? (
        <div className="mt-1 rounded border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11.5px] text-rose-900">
          ⚠️ <b>{payer}</b> มีบรรทัดค่าธรรมเนียม 1% — อนุมัติแล้ว<b>ค่าตอบแทนหมอจะไม่ถูกคำนวณใหม่</b>
          {Number.isFinite(Number(req.amount_after)) && Number.isFinite(Number(req.amount_before))
            ? ` · ส่วนต่างประมาณ ${fmt(Math.abs((Number(req.amount_after) - Number(req.amount_before)) * 0.01))} บาท`
            : ''}
          {' · '}ต้องไปตรวจที่สรุปเงินเดือนเอง
        </div>
      ) : null}

      {req.status === 'pending' && mayApprove ? (
        <div className="pt-0.5">
          <input
            id={`amtreq-note-${req.id}`}
            placeholder="เหตุผล (เฉพาะเมื่อไม่อนุมัติ)"
            className="h-7 w-52 rounded border border-slate-300 px-1.5 text-[11.5px] mr-1.5"
          />
          <button
            type="button"
            disabled={!!busy}
            onClick={() => decide('approve')}
            className="h-7 px-2 rounded bg-emerald-600 text-white text-[11.5px] hover:bg-emerald-700 disabled:opacity-40"
          >
            {busy === 'approve' ? '…' : 'อนุมัติ'}
          </button>
          <button
            type="button"
            disabled={!!busy}
            onClick={() => decide('reject')}
            className="ml-1 h-7 px-2 rounded border border-slate-300 text-[11.5px] text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            {busy === 'reject' ? '…' : 'ไม่อนุมัติ'}
          </button>
        </div>
      ) : null}

      {req.status === 'pending' && !mayApprove ? (
        <div className="text-[11.5px] text-slate-500">รอ GM หรือ Admin อนุมัติ</div>
      ) : null}

      {err ? <div className="text-[11.5px] text-rose-700">{err}</div> : null}
    </div>
  )
}

export { REASONS as AMOUNT_REASONS }
